import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Sparkles, Star, Music, Calendar, ArrowRight, 
  Heart, Users, Zap, TrendingUp, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { ArtistBadges } from '../components/VerificationBadges';

export default function Recommendations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [basedOn, setBasedOn] = useState(null);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/recommendations/artists?limit=8');
      setRecommendations(response.data.recommendations || []);
      setBasedOn(response.data.based_on);
    } catch (error) {
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async (artistId) => {
    try {
      await apiClient.post('/favorites', { artist_id: artistId });
      toast.success('Added to favorites!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Already in favorites');
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  };

  const getReasonIcon = (reason) => {
    if (reason?.includes('genre')) return <Music className="w-4 h-4" />;
    if (reason?.includes('price')) return <DollarSign className="w-4 h-4" />;
    if (reason?.includes('rated')) return <Star className="w-4 h-4" />;
    if (reason?.includes('Rising')) return <Zap className="w-4 h-4" />;
    if (reason?.includes('Popular')) return <TrendingUp className="w-4 h-4" />;
    return <Sparkles className="w-4 h-4" />;
  };

  if (loading && recommendations.length === 0) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4" data-testid="recommendations-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-8 h-8 text-violet-400" />
              <h1 className="text-3xl font-bold text-white">For You</h1>
            </div>
            <p className="text-white/60">Personalized artist recommendations based on your preferences</p>
          </div>
          <Button onClick={fetchRecommendations} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Preference Summary */}
        {basedOn && basedOn.preferred_genres?.length > 0 && (
          <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 mb-8">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-white/60">Based on:</span>
                {basedOn.preferred_genres?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-violet-400" />
                    <span className="text-white">{basedOn.preferred_genres.slice(0, 3).join(', ')}</span>
                  </div>
                )}
                {basedOn.total_bookings > 0 && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span className="text-white">{basedOn.total_bookings} past bookings</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personalized Recommendations */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" />
            Recommended For You
          </h2>
          
          {recommendations.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/60 mb-4">Book some artists to get personalized recommendations!</p>
                <Button onClick={() => navigate('/browse')} className="bg-violet-600 hover:bg-violet-500">
                  Browse Artists
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((artist) => (
                <Card 
                  key={artist.artist_id}
                  className="bg-white/5 border-white/10 hover:border-violet-500/50 transition-all cursor-pointer group overflow-hidden"
                  onClick={() => navigate(`/artists/${artist.artist_id}`)}
                >
                  <div className="relative h-40 bg-gradient-to-br from-violet-600/20 to-purple-600/20">
                    {artist.profile_image ? (
                      <img 
                        src={artist.profile_image} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFavorite(artist.artist_id);
                      }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-violet-600 transition-colors"
                    >
                      <Heart className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">
                          {artist.bio?.slice(0, 25) || 'Artist'}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <ArtistBadges artist={artist} size="xs" />
                        </div>
                      </div>
                      {artist.rating && (
                        <div className="flex items-center gap-1 text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm">{artist.rating}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {artist.genres?.slice(0, 2).map((genre) => (
                        <span 
                          key={genre}
                          className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-white/40">
                        {getReasonIcon(artist.recommendation_reason)}
                        <span className="truncate">{artist.recommendation_reason}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-violet-400 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
